import { ClbButton, ClbModal } from '@celebration/react';
import { useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import IOfferCardAcceptButtonProps from './IOfferCardAcceptButtonProps';
import { useOffersRound } from '../../../../../app/contexts/offers-round/offers-round.context';
import SelectAutocomplete from '../../../../../common/components/select-autocomplete';
import { formatCurrency } from '../../../../../utils/currency-utils';

export default function OfferCardAcceptButton({
    offer,
}: IOfferCardAcceptButtonProps) {
    const { acceptOffer, cancelOfferAccept, setAcceptedOffersRates } =
        useOffersRound();
    const { t } = useTranslation();

    const [isProcessingAcceptance, setIsProcessingAcceptance] = useState(false);
    const [isCancelModalOpened, setIsCancelModalOpened] = useState(false);
    const [choseVehicleModelOpened, setChoseVehicleModelOpened] =
        useState(false);
    const [chosenVehicle, setChosenVehicle] = useState<string | null>(null);
    const ratesByVehicle = useMemo(
        () => offer.load.ratesByVehicle ?? [],
        [offer.load.ratesByVehicle],
    );
    const hasEligibleRates = ratesByVehicle.length > 0;

    const chosenRate = ratesByVehicle.find(
        rate => rate.vehicle.name === chosenVehicle,
    );

    const handleOfferAcceptance = () => {
        if (!chosenRate) {
            return;
        }

        setIsProcessingAcceptance(true);

        acceptOffer(offer.loadOfferId, chosenRate.vehicle.name)
            .then(isAccepted => {
                if (!isAccepted) {
                    return;
                }

                setAcceptedOffersRates(prev => ({
                    ...prev,
                    [offer.loadOfferId]: chosenRate,
                }));

                setChoseVehicleModelOpened(false);
            })
            .finally(() => {
                setIsProcessingAcceptance(false);
            });
    };

    const handleOfferAcceptanceCancellation = () => {
        setIsCancelModalOpened(false);
        setIsProcessingAcceptance(true);

        cancelOfferAccept(offer.loadOfferId).then(() => {
            setIsProcessingAcceptance(false);
        });
    };

    const getVehicleOptions = useCallback(() => {
        return ratesByVehicle.map(rate => ({
            key: rate.vehicle.id,
            label: `${rate.vehicle.description} - ${formatCurrency(
                rate.chargeAmount,
                offer.load.shipment.currency,
            )}`,
            value: rate.vehicle.name,
        }));
    }, [offer.load.shipment.currency, ratesByVehicle]);

    const openVehicleChoiceModal = () => {
        setChosenVehicle(ratesByVehicle[0]?.vehicle.name ?? null);
        setChoseVehicleModelOpened(true);
    };

    return offer.accepted ? (
        <>
            <ClbButton
                size="sm"
                styleType="secondary"
                icon="cancel"
                label={t('SmartCargas.Rounds.CancelAccept')}
                data-testid="offer-footer-cancel-accept-button"
                onClick={() => setIsCancelModalOpened(true)}
                loading={isProcessingAcceptance}
                disabled={isProcessingAcceptance}
            />
            <ClbModal
                title={t('SmartCargas.Rounds.ConfirmAcceptanceCancelation')}
                open={isCancelModalOpened}
                onClose={() => setIsCancelModalOpened(false)}
                data-testid="offer-cancel-accept-modal"
                type="danger"
                itemsFooter={[
                    {
                        key: 'delete-button',
                        id: 'delete-button',
                        label: t('Sim'),
                        title: t('Sim'),
                        'data-testid': 'offer-cancel-accept-modal-yes',
                        onClick: () => handleOfferAcceptanceCancellation(),
                        role: 'button',
                        size: 'md',
                        styleType: 'danger',
                    },
                    {
                        key: 'delete-branch-cancel-button',
                        id: 'delete-branch-cancel-button',
                        label: t('Não'),
                        title: t('Não'),
                        'data-testid': 'offer-cancel-accept-modal-no',
                        onClick: () => setIsCancelModalOpened(false),
                        role: 'button',
                        size: 'md',
                        styleType: 'secondary',
                    },
                ]}
            />
        </>
    ) : (
        <>
            <ClbButton
                size="sm"
                styleType="primary"
                icon="check"
                label={t('SmartCargas.Rounds.Accept')}
                data-testid="offer-footer-accept-button"
                onClick={openVehicleChoiceModal}
                loading={isProcessingAcceptance}
                disabled={isProcessingAcceptance}
            />
            <ClbModal
                title={t(
                    hasEligibleRates
                        ? 'SmartCargas.Rounds.OfferVehicleChoiceTitle'
                        : 'SmartCargas.Rounds.OfferAcceptanceUnavailableTitle',
                )}
                description={t(
                    hasEligibleRates
                        ? 'SmartCargas.Rounds.OfferVehicleChoiceDescription'
                        : 'SmartCargas.Rounds.OfferVehicleChoiceNoEligibleRate',
                )}
                open={choseVehicleModelOpened}
                onClose={() => setChoseVehicleModelOpened(false)}
                data-testid="offer-accept-vehicle-modal"
                type="danger"
                itemsFooter={[
                    {
                        key: 'delete-button',
                        id: 'delete-button',
                        label: t('SmartCargas.Rounds.Accept'),
                        title: t('SmartCargas.Rounds.Accept'),
                        'data-testid': 'offer-accept-vehicle-modal-yes',
                        onClick: () => handleOfferAcceptance(),
                        disabled: !chosenRate || isProcessingAcceptance,
                        role: 'button',
                        size: 'md',
                        styleType: 'primary',
                    },
                    {
                        key: 'delete-branch-cancel-button',
                        id: 'delete-branch-cancel-button',
                        label: hasEligibleRates
                            ? t('SmartCargas.Rounds.CancelAccept')
                            : t('Fechar'),
                        title: hasEligibleRates
                            ? t('SmartCargas.Rounds.CancelAccept')
                            : t('Fechar'),
                        'data-testid': 'offer-accept-vehicle-modal-no',
                        onClick: () => setChoseVehicleModelOpened(false),
                        role: 'button',
                        size: 'md',
                        styleType: 'secondary',
                    },
                ]}
            >
                {hasEligibleRates ? (
                    <SelectAutocomplete
                        showSearch
                        id="offer-vehicle-choice"
                        key="offer-vehicle-choice"
                        label={t('SmartCargas.Rounds.OfferVehicleChoiceSelect')}
                        value={chosenVehicle}
                        onChange={(e: string) => setChosenVehicle(e)}
                        placeholder={t(
                            'SmartCargas.Rounds.OfferVehicleChoiceSelectPlaceholder',
                        )}
                        options={getVehicleOptions()}
                        data-testid="offer-vehicle-choice"
                        getPopupContainer={x => x.parentNode}
                        aria-required
                    />
                ) : undefined}
            </ClbModal>
        </>
    );
}
